import { Component } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { OptionsList } from "gung-common";
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { AuthService, SelectedCustomerService, authTypes } from 'gung-standard';
import { TranslateService } from '@ngx-translate/core';
import {
  MOUNT_SEVERITY_HIGH,
  MOUNT_SEVERITY_MEDIUM,
  OttoOlsenClaimService,
  OttoCreateClaimRequest,
  OttoCreateClaimResponse
} from '../../services/otto-olsen-claim.service';

@Component({
  selector: 'otto-olsen-create-full-order-claim',
  templateUrl: './otto-olsen-create-full-order-claim.component.html',
  styleUrl: './otto-olsen-create-full-order-claim.component.scss',
  standalone: false
})
export class OttoOlsenCreateFullOrderClaimComponent {
  public form: FormGroup;
  public orderId: string;
  protected images: File[] = [];
  protected currentUser: authTypes.User;
  protected customerNumber: string;
  protected customerName: string;
  public severityOptions: OptionsList[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private claimService: OttoOlsenClaimService,
    private authService: AuthService,
    private selectedCustomerService: SelectedCustomerService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.severityOptions = [
      { id: MOUNT_SEVERITY_MEDIUM, name: this.translate.instant('SEVERITY_MEDIUM') },
      { id: MOUNT_SEVERITY_HIGH, name: this.translate.instant('SEVERITY_HIGH') }
    ];
    this.initForm();
    this.route.paramMap.subscribe(params => {
      this.orderId = params.get('orderId');
    });

    this.authService.getCurrentUser().pipe(filter(user => !!user), first())
      .subscribe(user => this.currentUser = user);

    this.selectedCustomerService.getSelectedCustomer().pipe(filter(customer => !!customer), first())
      .subscribe(customer => {
        this.customerNumber = customer?.id;
        this.customerName = customer?.name;
      });
  }

  protected initForm() {
    this.form = this.formBuilder.group({
      description: this.formBuilder.control(null),
      severity: this.formBuilder.control(MOUNT_SEVERITY_MEDIUM),
      solution: this.formBuilder.control(null)
    });
  }

  protected imagePreviews: { file: File; url: string }[] = [];

  protected addImages(event) {
    const files: FileList = event?.target?.files;
    if (!files?.length) {
      return;
    }
    const added = Array.from(files);
    this.images = [...this.images, ...added];
    this.imagePreviews = [
      ...this.imagePreviews,
      ...added.map(file => ({ file, url: URL.createObjectURL(file) }))
    ];
    event.target.value = '';
  }

  protected removeImage(index: number) {
    URL.revokeObjectURL(this.imagePreviews[index].url);
    this.imagePreviews.splice(index, 1);
    this.images.splice(index, 1);
  }

  ngOnDestroy() {
    this.imagePreviews.forEach(p => URL.revokeObjectURL(p.url));
  }

  protected cancel() {
    this.router.navigate(['/orders/', this.orderId]);
  }

  protected postClaim(): Observable<OttoCreateClaimResponse> {
    const formRaw = this.form.getRawValue();

    const claim: OttoCreateClaimRequest = {
      customerNumber: this.customerNumber,
      customerName: this.customerName,
      orderNumber: this.orderId,
      invoiceNumber: null,
      userName: this.currentUser?.name,
      userEmail: this.currentUser?.email,
      description: formRaw.description,
      primaryReason: null,
      desiredSolution: formRaw.solution,
      severityId: formRaw.severity,
      lines: []
    };

    const result = this.claimService.createClaim(claim, this.images);
    result.subscribe(() => this.router.navigate(['/otto-olsen-claim-receipt/', this.orderId]));
    return result;
  }
}
